import { getFunctions, httpsCallable } from "firebase/functions";

export interface UserProfile {
  email: string;
  name: string;
  phoneNumber?: string;
  instagram?: string;
  createdAt: any;
  updatedAt: any;
  termsAcceptedAt?: any;
  termsAcceptedVersion?: string;
}

/**
 * Check if a user needs to accept the current version of Terms and Conditions
 * Uses graceful undefined handling for existing users without T&C fields
 */
export const requiresTermsAcceptance = (
  user: UserProfile | null | undefined,
  currentVersion: string
): boolean => {
  if (!user) return true;
  
  // Gracefully handle undefined fields (existing users before T&C implementation)
  const userVersion = user.termsAcceptedVersion ?? null;
  const acceptedAt = user.termsAcceptedAt ?? null;
  
  // User needs to accept if they haven't accepted at all, or if version doesn't match
  return userVersion !== currentVersion || acceptedAt === null;
};

/**
 * Call Cloud Function to accept Terms and Conditions with retry logic
 * @param version - The version of T&C being accepted
 * @returns Promise that resolves when acceptance is recorded
 */
export const acceptTerms = async (version: string): Promise<void> => {
  const functions = getFunctions();
  const acceptTermsAndConditions = httpsCallable(functions, "acceptTermsAndConditions");
  
  const maxRetries = 3;
  const baseDelay = 1000; // 1 second
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const result = await acceptTermsAndConditions({ version });
      
      if ((result.data as any).success) {
        return; // Success!
      } else {
        throw new Error((result.data as any).message || "Failed to accept terms");
      }
    } catch (error: any) {
      const isLastAttempt = attempt === maxRetries;
      
      if (isLastAttempt) {
        // On final attempt, throw a user-friendly error
        if (error.code === "unauthenticated") {
          throw new Error("You must be logged in to accept terms. Please log out and log in again.");
        } else if (error.code === "permission-denied") {
          throw new Error("Permission denied. Please contact support at ciaofridays@gmail.com");
        } else if (error.message.includes("network")) {
          throw new Error("Network error. Please check your internet connection and try again.");
        } else {
          throw new Error(
            error.message || "Failed to save your acceptance. Please try again or contact support."
          );
        }
      }
      
      // Exponential backoff before retry
      const delay = baseDelay * Math.pow(2, attempt - 1);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

/**
 * Call Cloud Function to reset all users' terms acceptance (admin only)
 * @returns Promise with count of affected users
 */
export const resetAllTermsAcceptance = async (): Promise<number> => {
  const functions = getFunctions();
  const resetTerms = httpsCallable(functions, "resetAllTermsAcceptance");
  
  try {
    const result = await resetTerms();
    const data = result.data as any;
    
    if (data.success) {
      return data.affectedUsers || 0;
    } else {
      throw new Error(data.message || "Failed to reset terms acceptance");
    }
  } catch (error: any) {
    if (error.code === "permission-denied") {
      throw new Error("Only administrators can reset terms acceptance");
    } else if (error.message.includes("network")) {
      throw new Error("Network error. Please check your internet connection.");
    } else {
      throw new Error(error.message || "Failed to reset terms. Please try again.");
    }
  }
};
